import { BasketButtonError, } from '@as-react/common-types';
import { capitalizeEach, capitalizeOnly, findColor, findSize, isNullOrUndefined } from '@as-react/common-utils';
import { I18N_NAMESPACES } from '../constants/i18nConstants';
import { PRODUCT_URL_PREFIX } from '../constants/ProductConstants';
import { BasketActionTypes } from '../enums/ActionEnums';
import AsNumeral from '../vendors/asNumeral';
const FEATURE_VALUE_YES = {
    nl: 'ja',
    en: 'yes',
    fr: 'oui',
};
const MAX_PRODUCT_DISCOUNT_AMOUNT = 20;
const SIZELENGTH_ORDER = {
    Short: 0,
    Regular: 1,
    Long: 2,
    'Extra Long': 3,
};
export const getProductDiscount = (discountAmount, discountPercentage, currency) => !!discountAmount && !!discountPercentage
    ? Number(discountAmount) < MAX_PRODUCT_DISCOUNT_AMOUNT
        ? `${discountPercentage}%`
        : `${currency}${AsNumeral.formatAsCurrency(discountAmount)}`
    : '';
export const getDefaultSize = (colors, colorId, sizeCode) => {
    const defaultColorVariation = findColor(colors, colorId);
    return defaultColorVariation ? findSize(defaultColorVariation.sizes, sizeCode) : null;
};
export const setDeliveryPromiseToProducts = (products, payload) => {
    const { productId, deliveryPromise, colourId } = payload;
    return products.map((product) => {
        if (product.productId === Number(productId) && product.colourId === colourId) {
            return Object.assign(Object.assign({}, product), { deliveryPromise });
        }
        return product;
    });
};
export const isExtensiveProductTile = (productTileFormat) => productTileFormat === 'extensive';
export const isProductOutOfStock = (product) => product.availability <= 0;
export const isProductWithColorOutOfStock = (product) => { var _a, _b; return Number((_b = (_a = product.colours) === null || _a === void 0 ? void 0 : _a.find(item => item.colourId === product.colourId)) === null || _b === void 0 ? void 0 : _b.availability) <= 0; };
export const isClickAndCollectOnly = (product) => Boolean(product.hdExcluded) && !product.ccExcluded;
export const isInStoreOnly = (product) => Boolean(product.hdExcluded) && Boolean(product.ccExcluded);
export const getSizeDescription = (size, useOriginalSize) => useOriginalSize
    ? size.originalSize || size.code
    : size.fullCommercialSize || size.commercialSize || size.code;
export const getProductDetailUrl = ({ seoUrl, colourId, activeSize, isRecommended, additionalQueryParameters, }, root) => root
    .concat((seoUrl === null || seoUrl === void 0 ? void 0 : seoUrl.indexOf('/')) === 0 ? PRODUCT_URL_PREFIX + seoUrl : PRODUCT_URL_PREFIX + '/' + seoUrl)
    .concat('.html')
    .concat(colourId ? `?colour=${colourId}` : '')
    .concat(activeSize ? `&productSize=${activeSize}` : '')
    .concat(isRecommended ? '&rrec=true' : '')
    .concat(additionalQueryParameters ? additionalQueryParameters.replace('?', '&') : '');
export const makeCommercialSize = (size) => [size.commercialSizePrefix, size.commercialSizeDefault, size.commercialSizeSuffix].filter(Boolean).join(' ');
export const makeOriginalSize = (size) => [size.internalSizePrefix, size.originalSize, size.internalSizeSuffix].filter(Boolean).join(' ');
export const isSoldOutSize = (size) => Number(size.availability) <= 0 && size.historicAvailable;
export const selectOptionLabel = (size, showFullCommercialSize = true) => {
    let label = size.fullCommercialSize || size.commercialSize || size.code;
    if (!showFullCommercialSize) {
        label = size.commercialSizeDefault ? makeCommercialSize(size) : makeOriginalSize(size);
    }
    return label;
};
const selectFullOptionLabel = (size) => {
    var _a, _b, _c, _d;
    const fullCommercialSize = (_a = size.fullCommercialSize) === null || _a === void 0 ? void 0 : _a.trim();
    const [combinedCommercialSize, ...combinedInternalSizeParts] = (_b = fullCommercialSize === null || fullCommercialSize === void 0 ? void 0 : fullCommercialSize.split('|').map(sizePart => sizePart.trim())) !== null && _b !== void 0 ? _b : [];
    const combinedInternalSize = combinedInternalSizeParts.join(' | ').trim();
    if (combinedCommercialSize && combinedInternalSize) {
        return `${combinedCommercialSize} (${combinedInternalSize})`;
    }
    const commercialSize = makeCommercialSize(size) || ((_c = size.commercialSize) === null || _c === void 0 ? void 0 : _c.trim()) || fullCommercialSize || ((_d = size.code) === null || _d === void 0 ? void 0 : _d.trim()) || '';
    const internalSize = makeOriginalSize(size).trim();
    if (!internalSize || commercialSize === internalSize || commercialSize.endsWith(`(${internalSize})`)) {
        return commercialSize;
    }
    return `${commercialSize} (${internalSize})`;
};
export const mapSizeSelectOptions = (sizes, showSoldOutSizes, skuWithoutFiltering, allowSelectSoldOutSizes, showFullCommercialSize = true) => {
    const filteredSizes = sizes.filter(size => {
        if (skuWithoutFiltering === parseInt(size.sku, 10)) {
            return true;
        }
        return !showSoldOutSizes
            ? size.active && Number(size.availability) > 0
            : (size.active && Number(size.availability) > 0) ||
                (size.active && Number(size.availability) <= 0 && size.historicAvailable);
    });
    const options = filteredSizes.map(size => {
        const isSoldOut = isSoldOutSize(size);
        const isNotifyMe = !size.storeSourceExcluded && isSoldOut;
        return Object.assign({ label: selectOptionLabel(size, showFullCommercialSize), value: size.sku, disabled: !allowSelectSoldOutSizes && isSoldOut && skuWithoutFiltering !== parseInt(size.sku, 10), isNotifyMe }, (allowSelectSoldOutSizes ? { isSoldOut } : {}));
    });
    return { filteredSizes, options };
};
export const mapAndesSizeSelectOptions = (sizes, showSoldOutSizes, skuWithoutFiltering, allowSelectSoldOutSizes, showFullCommercialSize = true) => {
    const filteredSizes = sizes.filter(size => {
        if (skuWithoutFiltering === parseInt(size.sku, 10)) {
            return true;
        }
        return !showSoldOutSizes
            ? size.active && Number(size.availability) > 0
            : (size.active && Number(size.availability) > 0) ||
                (size.active && Number(size.availability) <= 0 && size.historicAvailable);
    });
    const options = filteredSizes.map(size => {
        const isSoldOut = isSoldOutSize(size);
        const isDisabled = !allowSelectSoldOutSizes && isSoldOut && skuWithoutFiltering !== parseInt(size.sku, 10);
        return {
            id: size.sku,
            text: showFullCommercialSize ? selectFullOptionLabel(size) : selectOptionLabel(size, false),
            disabled: isDisabled,
            strikethrough: isDisabled || allowSelectSoldOutSizes ? isSoldOut : undefined,
        };
    });
    return options;
};
export const splitSizesBySizeAndLength = (sizes, showFullCommercialSize = false) => {
    const sizesMap = new Map();
    const lengthsMap = new Map();
    sizes.forEach(s => {
        const label = selectOptionLabel(s, showFullCommercialSize);
        const { size, length } = extractLengthAndSize(label);
        if (size) {
            if (!sizesMap.has(size)) {
                sizesMap.set(size, []);
            }
            sizesMap.get(size).push(s.sku);
        }
        if (length) {
            if (!lengthsMap.has(length)) {
                lengthsMap.set(length, []);
            }
            lengthsMap.get(length).push(s.sku);
        }
    });
    const sortedLengthsMap = new Map([...lengthsMap.entries()].sort((a, b) => SIZELENGTH_ORDER[a[0]] - SIZELENGTH_ORDER[b[0]]));
    const totalSizesAmount = [...sizesMap.values()].reduce((sum, value) => sum + (value.length !== undefined ? value.length : 0), 0);
    const totalLengthsAmount = [...lengthsMap.values()].reduce((sum, value) => sum + (value.length !== undefined ? value.length : 0), 0);
    const isSizeAndLengthSplitted = sizesMap.size > 0 && sortedLengthsMap.size > 0 && totalSizesAmount === totalLengthsAmount;
    return { isSizeAndLengthSplitted, sizesMap, lengthsMap: sortedLengthsMap };
};
export const extractLengthAndSize = (originalSize) => {
    const matchSize = originalSize.match(/short|regular|long|extra long/i);
    const length = matchSize ? capitalizeEach(matchSize[0]) : '';
    const size = originalSize.replace(new RegExp(length, 'i'), '').trim();
    return { length, size };
};
export const generateUniqueSizeOptions = (options) => {
    const uniqueLabelsMap = new Map();
    options
        .map(option => (Object.assign(Object.assign({}, option), { label: extractLengthAndSize(option.label.toString()).size })))
        .forEach((option, _, allOptions) => {
        const sameLabelOptions = allOptions.filter(singleOption => singleOption.label === option.label);
        const allSoldOut = sameLabelOptions.every(opt => opt.isSoldOut);
        const allDisabled = sameLabelOptions.every(opt => opt.disabled);
        if (!uniqueLabelsMap.has(option.label)) {
            uniqueLabelsMap.set(option.label, {
                label: option.label,
                value: '',
                disabled: allDisabled,
                isSoldOut: allSoldOut,
                isNotifyMe: false,
            });
        }
    });
    return Array.from(uniqueLabelsMap.values());
};
export const generateUniqueLengthOptions = (options, lengthsMap) => [...lengthsMap.keys()].map(value => {
    var _a;
    const allSoldOut = (_a = lengthsMap.get(value)) === null || _a === void 0 ? void 0 : _a.every(sku => { var _a; return (_a = options.find(opt => opt['value'] === sku)) === null || _a === void 0 ? void 0 : _a.isSoldOut; });
    return {
        label: value,
        value,
        disabled: !lengthsMap.has(value),
        isSoldOut: allSoldOut,
        isNotifyMe: false,
    };
});
export const getActiveSizeOptions = (values = [], options, typedOptions, type) => {
    const activeOptions = values.map(value => {
        const opt = options.find(opt => opt['value'] === value);
        return {
            label: extractLengthAndSize((opt === null || opt === void 0 ? void 0 : opt.label.toString()) || '')[type],
            value,
            disabled: opt === null || opt === void 0 ? void 0 : opt.disabled,
            isSoldOut: opt === null || opt === void 0 ? void 0 : opt.isSoldOut,
            isNotifyMe: opt === null || opt === void 0 ? void 0 : opt.isNotifyMe,
        };
    });
    const updatedOptions = typedOptions.map(typeOpt => {
        const opt = activeOptions.find(opt => opt['label'] === typeOpt.label);
        if (opt && typeOpt.label === opt.label) {
            return opt;
        }
        return Object.assign(Object.assign({}, typeOpt), { disabled: true, isSoldOut: false, isNotifyMe: false });
    });
    return updatedOptions;
};
export const getOptionIndex = (options, selectedSize, selectedLength) => options.findIndex(option => {
    const { length, size } = extractLengthAndSize(option.label.toString());
    return `${size} ${length}`.toLowerCase().trim() === `${selectedSize} ${selectedLength}`.toLowerCase().trim();
});
export const getSizeSelectOptions = (sizes, showSoldOutSizes, skuWithoutFiltering) => mapSizeSelectOptions(sizes, showSoldOutSizes, skuWithoutFiltering);
export const getAndesSizeSelectOptions = (sizes, showSoldOutSizes, skuWithoutFiltering) => mapAndesSizeSelectOptions(sizes, showSoldOutSizes, skuWithoutFiltering);
export const filterProductCategoryEmptyFeatures = (category) => {
    var _a;
    return (Object.assign(Object.assign({}, category), { features: (_a = category.features) === null || _a === void 0 ? void 0 : _a.filter(feature => !isNullOrUndefined(feature.featureValue) && feature.featureValue.length !== 0) }));
};
export const filterProductCategoryFalseBooleanFeatures = (category, lang) => {
    var _a;
    return (Object.assign(Object.assign({}, category), { features: (_a = category.features) === null || _a === void 0 ? void 0 : _a.filter(feature => {
            var _a;
            return feature.featureType !== 'BOOLEAN' ||
                ((_a = feature.featureValue) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === FEATURE_VALUE_YES[lang];
        }) }));
};
// override hasDiscount because we only need to show label in swatch when it's promo price, not when bundle discount
export const getSwatchColors = (colours) => colours.map(colour => {
    var _a;
    return (Object.assign(Object.assign({}, colour), { hasDiscount: (_a = colour.promoDiscountPresent) !== null && _a !== void 0 ? _a : colour.hasDiscount }));
});
export const getAvailableSizes = (sizes = []) => sizes.filter(({ active, availability = 0 }) => active && availability > 0);
export const getSizeButtonsError = (basketValidationState, type) => {
    switch (basketValidationState) {
        case BasketButtonError.SKU_OPEN_SIZE_SELECT:
            if (type) {
                if (type === 'size')
                    return 'pdp.size.buttons.size.error';
                if (type === 'length')
                    return 'pdp.size.buttons.length.error';
            }
            return 'pdp.size.buttons.error';
        case BasketButtonError.SKU:
            if (type) {
                if (type === 'size')
                    return 'pdp.size.buttons.storestock.size.error';
                if (type === 'length')
                    return 'pdp.size.buttons.storestock.length.error';
            }
            return 'pdp.size.buttons.storestock.error';
        default:
            return 'pdp.size.buttons.notifyme.error';
    }
};
export const createProductDetailToProductListerAdapter = (product) => {
    var _a, _b, _c, _d, _e;
    const { productColorVariations } = product;
    const colours = productColorVariations
        ? productColorVariations.map(productColorVariation => {
            var _a;
            return ({
                allColourImages: productColorVariation.images ? productColorVariation.images.map(image => image.picture) : [],
                alwaysVisible: productColorVariation.alwaysVisible || false,
                availability: productColorVariation.availability || 0,
                colourCode: productColorVariation.colorCode,
                colourId: Number(productColorVariation.colorId),
                ccExcluded: productColorVariation.ccExcluded,
                description: (productColorVariation.translations ? productColorVariation.translations.description : '') || '',
                extendedRange: false,
                hasDifferentPrices: productColorVariation.hasDifferentPrices || false,
                hasDiscount: productColorVariation.hasDiscount || false,
                hoverImage: undefined,
                image: productColorVariation.images && productColorVariation.images.length > 0
                    ? ((_a = productColorVariation.images[0]) === null || _a === void 0 ? void 0 : _a.picture) || ''
                    : '',
                isCompareProduct: false,
                markdown: productColorVariation.markdown || false,
                newProduct: productColorVariation.newProduct,
                price: {
                    productDiscountPercentage: productColorVariation.colourDiscountPercentage,
                    productDiscountAmount: productColorVariation.colourDiscountAmount,
                },
                priceOverlay: productColorVariation.priceOverlay,
                prices: productColorVariation.prices,
                sizes: productColorVariation.sizes,
                swatch: productColorVariation.swatch,
            });
        })
        : [];
    return Object.assign(Object.assign({}, product), { availability: product.productAvailability || 0, brand: ((_a = product.brand) === null || _a === void 0 ? void 0 : _a.description) || '', colourId: Number(product.defaultColor), colours, defaultCategoryId: Number(product.defaultCategoryId), productId: Number(product.productId), productRating: (_b = product.productReview) === null || _b === void 0 ? void 0 : _b.avgScore, productReviews: (_c = product.productReview) === null || _c === void 0 ? void 0 : _c.numberOfScores, title: product.productTranslatedProperties && 'title' in product.productTranslatedProperties
            ? ((_d = product.productTranslatedProperties) === null || _d === void 0 ? void 0 : _d.title) || ''
            : '', seoUrl: product.productTranslatedProperties && 'seoUrl' in product.productTranslatedProperties
            ? ((_e = product.productTranslatedProperties) === null || _e === void 0 ? void 0 : _e.seoUrl) || ''
            : '' });
};
const isSameBasketProduct = (product, target) => {
    var _a, _b;
    return product.sku.toString() === target.sku &&
        product.productId.toString() === target.productId &&
        product.colorId === target.colorId &&
        (((_a = product.additionalProperties) === null || _a === void 0 ? void 0 : _a.eVoucherId)
            ? product.additionalProperties.eVoucherId === ((_b = target.additionalProperties) === null || _b === void 0 ? void 0 : _b.eVoucherId)
            : true);
};
export const filterBasketProducts = (products, productToRemove) => products.filter(product => !isSameBasketProduct(product, productToRemove));
export const updateBasketProductQuantity = (products, productToUpdate, actionType) => products.map(product => {
    if (isSameBasketProduct(product, productToUpdate)) {
        const quantity = actionType === BasketActionTypes.INCREASE_QUANTITY
            ? product.quantity + 1
            : actionType === BasketActionTypes.DECREASE_QUANTITY
                ? product.quantity - 1
                : productToUpdate.quantity;
        return Object.assign(Object.assign({}, product), { quantity });
    }
    return product;
});
export const getSizeColorLabel = (product, t) => {
    const color = product.color ? capitalizeOnly(product.color) : '';
    const size = product.sizeDescription || product.originalSize;
    if (size !== '.' && color) {
        return t(`${I18N_NAMESPACES.navigation}:basket.flyout.options`, {
            size: size,
            color: color,
        });
    }
    else if (size !== '.' && !color) {
        return t(`${I18N_NAMESPACES.navigation}:basket.flyout.options.size`, {
            size: size,
        });
    }
    else if ((size === '.' && color) || !size) {
        return t(`${I18N_NAMESPACES.navigation}:basket.flyout.options.color`, {
            color: color,
        });
    }
    return '';
};
